import type { TextNodeType } from "../../../components/FormattedText/FormattedText";
import type { IBenefitsBottomsheet } from "./components/PlanCards/types";

/**
 * Adapts the revamp `benefitsBottomsheet` API shape into the
 * `moreBenefits` object (`{ [groupName]: IBenefits[] }`) that the
 * existing `MoreBenefits` / `BenefitItem` components expect.
 */
export function toMoreBenefitsData(data: IBenefitsBottomsheet): {
  moreBenefits: Record<
    string,
    {
      icon?: string;
      title?: { text: string };
      price?: { text: string };
      info?: { text: string };
    }[]
  >;
  packName?: TextNodeType;
  insuranceType?: TextNodeType;
  vendorDisplayPrefix?: string;
  vendorDisplayName?: string;
  insPlanName?: TextNodeType;
  tncLink?: string;
  ctaLink?: string;
  insAmount?: { text: string };
  paxPerDay?: TextNodeType;
  buyBtn?: string;
  planExtraInfo?: string;
  service?: string;
} {
  const moreBenefits: Record<
    string,
    {
      icon?: string;
      title?: { text: string };
      price?: { text: string };
      info?: { text: string };
    }[]
  > = {};

  data.benefits?.forEach((group) => {
    if (!group.name || !group.features?.length) return;
    moreBenefits[group.name] = group.features.map((f) => ({
      icon: f.icon,
      title: f.name ? { text: f.name } : undefined,
      price: f.cover ? { text: f.cover } : undefined,
      info: f.description ? { text: f.description } : undefined,
    }));
  });

  const { planInfo } = data;

  return {
    moreBenefits,
    packName: planInfo?.packName,
    insuranceType: planInfo?.name,
    vendorDisplayPrefix: planInfo?.vendorDisplayPrefix,
    vendorDisplayName: planInfo?.vendorDisplayName,
    insPlanName: planInfo?.insPlanName,
    tncLink: planInfo?.tncLink,
    ctaLink: planInfo?.ctaLink,
    insAmount: planInfo?.price
      ? { text: `${planInfo.currency ?? ""}${planInfo.price}` }
      : undefined,
    paxPerDay: planInfo?.unit,
    buyBtn: planInfo?.buyBtn,
    planExtraInfo: planInfo?.planExtraInfo,
    service: planInfo?.service,
  };
}

/**
 * Reads the current listing page URL and merges product-specific identifiers
 * into a single params object suitable for the traveller-details page.
 *
 * Safe to call only on the client (returns empty object during SSR).
 */
export function buildTravellerDetailsParams(
  productId: string | number,
  quoteId: string
): Record<string, string | boolean> {
  if (typeof window === "undefined") return {};

  const searchParams = new URLSearchParams(window.location.search);
  const queryParams = Object.fromEntries(searchParams.entries());

  return {
    ...queryParams,
    countries: searchParams.get("countries") ?? "",
    startDate: searchParams.get("startDate") ?? "",
    endDate: searchParams.get("endDate") ?? "",
    travellers: searchParams.get("travellers") ?? "",
    isUpcomingTrip: searchParams.has("isUpcomingTrip")
      ? searchParams.get("isUpcomingTrip") ?? "false"
      : "false",
    productId: String(productId),
    quoteId,
  };
}
